import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import clsx from 'clsx';
import { Card } from '../../layout/Card.jsx';
import { Icon } from '../../layout/Icon.jsx';
import { Pill } from '../../layout/Pill.jsx';
import { api } from '../../shared/api.js';
import { formatIQD, formatDateTime } from '../../shared/format.js';
import { useAuth } from '../../shared/useAuth.js';
import { useShiftStatus, useShiftUI, useApproveVariance } from '../../shared/useShift.js';
import { ViewAllToggle, ViewAllNotice } from '../../shared/ViewAllToggle.jsx';
import { VIEW_ALL_LIMIT } from '../../shared/viewAll.js';

const TABS = [
  { key: '', label: 'الكل' },
  { key: 'open', label: 'مفتوح' },
  { key: 'closed', label: 'مغلق' }
];

function durationLabel(from, to) {
  const ms = new Date(to || Date.now()).getTime() - new Date(from).getTime();
  const min = Math.max(0, Math.round(ms / 60000));
  const h = Math.floor(min / 60);
  return h ? `${h} س ${min % 60} د` : `${min} د`;
}

function VarianceCell({ shift }) {
  if (shift.status === 'open') return <span className="text-ink-3">—</span>;
  const v = shift.variance;
  if (v == null) return <span className="text-ink-3">مخفي</span>;
  if (v === 0) return <span className="text-green font-bold">مطابق ✓</span>;
  return (
    <span className="font-bold" style={{ color: v > 0 ? '#1D4ED8' : '#B91C1C' }}>
      {v > 0 ? '+' : ''}{formatIQD(v)}
      {shift.varianceApproved ? <span className="text-green text-[11px] ms-1">✓ معتمد</span> : null}
    </span>
  );
}

export function ShiftsScreen() {
  const hasPermission = useAuth((s) => s.hasPermission);
  const me = useAuth((s) => s.user);
  const canManage = hasPermission('shifts.manage');
  const canApprove = hasPermission('shifts.approve_variance');
  const { shift: myShift } = useShiftStatus();
  const openModal = useShiftUI((s) => s.openModal);
  const approve = useApproveVariance();

  const [status, setStatus] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [q, setQ] = useState('');
  const [showAll, setShowAll] = useState(false);

  const params = useMemo(() => {
    const p = { limit: showAll ? VIEW_ALL_LIMIT : 200 };
    if (status) p.status = status;
    if (from) p.from = new Date(`${from}T00:00:00`).toISOString();
    if (to) p.to = new Date(`${to}T23:59:59`).toISOString();
    return p;
  }, [status, from, to, showAll]);

  const listQ = useQuery({
    queryKey: ['shifts', 'list', params],
    queryFn: async () => {
      const { data } = await api.get('/shifts', { params });
      return data;
    },
    refetchInterval: 60_000
  });

  const all = listQ.data?.shifts || [];
  const shifts = q.trim()
    ? all.filter((s) => `${s.cashierName} ${s.cashboxName}`.includes(q.trim()))
    : all;
  const stats = listQ.data?.stats;

  return (
    <div className="space-y-3.5">
      {myShift ? (
        <div className="card p-3.5 flex items-center gap-3" style={{ background: '#F0FDF4', borderColor: '#BBF7D0' }}>
          <span className="w-2.5 h-2.5 rounded-full" style={{ background: '#22C55E' }} />
          <div className="flex-1 text-[13px]">
            <b>شفت مفتوح حالياً</b> — {myShift.cashboxName} • منذ {formatDateTime(myShift.openedAt)}
          </div>
          <button type="button" className="btn" onClick={() => openModal('report', { reportShiftId: myShift._id })}>
            <Icon name="print" size={13} /> تقرير
          </button>
          <button type="button" className="btn btn-danger" onClick={() => openModal('close', { closeShiftId: myShift._id })}>
            إقفال
          </button>
        </div>
      ) : (
        <div className="card p-3.5 flex items-center gap-3" style={{ background: '#FFF7ED', borderColor: '#FED7AA' }}>
          <Icon name="clock" size={16} color="#EA580C" />
          <div className="flex-1 text-[13px]">لا يوجد لديك شفت مفتوح.</div>
          <button type="button" className="btn btn-primary" onClick={() => openModal('open')}>فتح شفت</button>
        </div>
      )}

      {stats ? (
        <div className="grid grid-cols-4 gap-3">
          {[
            { label: 'شفتات مفتوحة', value: stats.open, color: '#16A34A' },
            { label: 'شفتات مغلقة', value: stats.closed, color: '#6B7280' },
            {
              label: 'إجمالي الفروقات',
              value: formatIQD(stats.variance || 0),
              color: (stats.variance || 0) < 0 ? '#B91C1C' : (stats.variance || 0) > 0 ? '#1D4ED8' : '#15803D'
            },
            { label: 'فروقات بانتظار الاعتماد', value: stats.unapproved, color: stats.unapproved ? '#EA580C' : '#6B7280' }
          ].map((k) => (
            <div key={k.label} className="card p-3.5">
              <div className="text-[11.5px] text-ink-3">{k.label}</div>
              <div className="text-[20px] font-extrabold mt-1" style={{ color: k.color }}>{k.value}</div>
            </div>
          ))}
        </div>
      ) : null}

      <Card>
        <div className="flex items-center gap-2 flex-wrap mb-3">
          <div className="inline-flex items-center gap-1 p-1 bg-paper-2 rounded-[10px]">
            {TABS.map((t) => (
              <button
                key={t.key}
                type="button"
                onClick={() => setStatus(t.key)}
                className={clsx(
                  'px-3 py-1.5 rounded-[8px] text-[12.5px] font-semibold',
                  status === t.key ? 'bg-orange text-white' : 'text-ink-2 hover:bg-white'
                )}
              >
                {t.label}
              </button>
            ))}
          </div>
          <label className="text-[12px] text-ink-3">من</label>
          <input type="date" className="input" style={{ width: 150 }} value={from} onChange={(e) => setFrom(e.target.value)} />
          <label className="text-[12px] text-ink-3">إلى</label>
          <input type="date" className="input" style={{ width: 150 }} value={to} onChange={(e) => setTo(e.target.value)} />
          <input className="input" style={{ width: 200 }} placeholder="بحث بالكاشير أو القاصة..." value={q} onChange={(e) => setQ(e.target.value)} />
          <div className="flex items-center gap-2 ms-auto">
            <ViewAllToggle
              all={showAll}
              onChange={setShowAll}
              shown={shifts.length}
              total={listQ.data?.total}
            />
            <button type="button" className="btn" onClick={() => listQ.refetch()}>
              <Icon name="clock" size={13} /> تحديث
            </button>
          </div>
        </div>

        {listQ.isLoading ? (
          <div className="text-center py-10 text-ink-3">جاري التحميل...</div>
        ) : !shifts.length ? (
          <div className="text-center py-10 text-ink-3">لا توجد شفتات</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-[12.5px]">
              <thead>
                <tr className="text-ink-3 border-b border-line text-right">
                  <th className="p-2">#</th>
                  <th className="p-2">الكاشير</th>
                  <th className="p-2">الدرج</th>
                  <th className="p-2">الحالة</th>
                  <th className="p-2">العهدة</th>
                  <th className="p-2">المتوقع</th>
                  <th className="p-2">المعدود</th>
                  <th className="p-2">الفرق</th>
                  <th className="p-2">المدة</th>
                  <th className="p-2">الفتح</th>
                  <th className="p-2">إجراءات</th>
                </tr>
              </thead>
              <tbody>
                {shifts.map((s) => {
                  const id = s._id || s.id;
                  const mine = String(s.cashierId) === String(me?._id || me?.id);
                  const needsApproval = s.status === 'closed' && s.variance && !s.varianceApproved;
                  return (
                    <tr key={id} className="border-b border-line">
                      <td className="p-2 font-mono text-ink-3">{String(id).slice(-6).toUpperCase()}</td>
                      <td className="p-2 font-bold">{s.cashierName}</td>
                      <td className="p-2">{s.cashboxName}</td>
                      <td className="p-2">
                        {s.status === 'open' ? <Pill variant="success">مفتوح</Pill> : <Pill variant="muted">مغلق</Pill>}
                      </td>
                      <td className="p-2">
                        {formatIQD(s.openingBalance || 0)}
                        {s.openingCarry?.mismatch ? <Pill variant="warning" className="ms-1 !text-[10px]">⚠ عدم مطابقة</Pill> : null}
                      </td>
                      <td className="p-2">{s.expectedCash == null ? '—' : formatIQD(s.expectedCash)}</td>
                      <td className="p-2">{s.countedCash == null ? '—' : formatIQD(s.countedCash)}</td>
                      <td className="p-2"><VarianceCell shift={s} /></td>
                      <td className="p-2">{durationLabel(s.openedAt, s.closedAt)}</td>
                      <td className="p-2 text-ink-3">{formatDateTime(s.openedAt)}</td>
                      <td className="p-2">
                        <div className="flex gap-1">
                          <button
                            type="button"
                            className="btn btn-sm"
                            title="تقرير الشفت"
                            onClick={() => openModal('report', { reportShiftId: id })}
                          >
                            <Icon name="reports" size={13} />
                          </button>
                          {s.status === 'open' && (mine || canManage) ? (
                            <button
                              type="button"
                              className="btn btn-sm btn-danger"
                              title={mine ? 'إقفال الشفت' : 'إقفال إجباري'}
                              onClick={() => openModal('close', { closeShiftId: id })}
                            >
                              إقفال
                            </button>
                          ) : null}
                          {needsApproval && canApprove && !mine ? (
                            <button
                              type="button"
                              className="btn btn-sm btn-primary"
                              disabled={approve.isPending}
                              onClick={() => approve.mutate(id)}
                            >
                              اعتماد الفرق
                            </button>
                          ) : null}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        <ViewAllNotice all={showAll} shown={shifts.length} total={listQ.data?.total} />
      </Card>
    </div>
  );
}
